/**
 * SleepDiaryPage
 *
 * Displays a resident's sleep-health dashboard containing:
 *  - Four key metric cards (total sleep, efficiency, WASO, latency)
 *  - Sleep stages donut chart
 *  - Body movement lollipop bar chart
 *  - Sleep duration line chart
 *  - Wake episodes timeline
 *
 * Data is fetched from the API on mount (or whenever `residentId` changes)
 * and rendered with Recharts inside a responsive 1 / 2 / 3-column grid.
 */

import { useState, useEffect, useCallback } from "react";

import wakeIcon       from "../assets/wake_icon.svg";
import totalSleepIcon from "../assets/total_sleep_time.svg";
import efficiencyIcon from "../assets/sleep_efficiency.svg";
import latencyIcon    from "../assets/sleep_latency.svg";
import wasoIcon       from "../assets/wake_after_sleep.svg";

import useWindowSize  from "../hooks/useWindowSize";
import apiService     from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorMessage   from "../components/ErrorMessage";
import "../styles/pages/SleepDiaryPage.css";

import {
  PieChart, Pie, Cell,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  ResponsiveContainer,
  BarChart, Bar,
} from "recharts";

/* ─────────────────────────────────────────────────────────────────────────
   Module-level constants
   ───────────────────────────────────────────────────────────────────────── */

/**
 * Fixed hourly slots that make up the overnight sleep window (10 PM – 6 AM).
 * Each slot is matched against wake-episode data returned by the backend.
 *
 * @type {{ hour: number, label: string }[]}
 */
const SLEEP_TIMELINE = [
  { hour: 22, label: "10PM" },
  { hour: 23, label: "11PM" },
  { hour: 0,  label: "12AM" },
  { hour: 1,  label: "1AM"  },
  { hour: 2,  label: "2AM"  },
  { hour: 3,  label: "3AM"  },
  { hour: 4,  label: "4AM"  },
  { hour: 5,  label: "5AM"  },
  { hour: 6,  label: "6AM"  },
];

/**
 * X-axis tick labels for the wake-episodes duration ruler (0 – 60 min).
 *
 * @type {string[]}
 */
const WAKE_DURATION_TICKS = ["0Min", "10Min", "20Min", "30Min", "40Min", "50Min", "60Min"];

/**
 * Legend entries for the body-movement colour gradient.
 * Each tuple is [hex background colour, display label].
 *
 * @type {[string, string][]}
 */
const MOVEMENT_LEGEND = [
  ["#ffffff", "No Movement"],
  ["#1578be", "Moderate Movement"],
  ["#0a2240", "High Movement"],
];



/* ─────────────────────────────────────────────────────────────────────────
   Shared sub-components
   ───────────────────────────────────────────────────────────────────────── */

/**
 * GCard — Gradient card container used for every chart panel.
 *
 * Renders a dark navy gradient background with a subtle bottom glow
 * and a white 1 px border. All child content sits above the glow layer
 * via z-index.
 *
 * @param {{ children: React.ReactNode, className?: string, isMobile?: boolean }} props
 */
const GCard = ({ children, className = "", isMobile = false }) => (
  <div className={`sleep-diary-gcard ${isMobile ? 'mobile' : ''} ${className}`}>
    {/* Ambient bottom glow — purely decorative */}
    <div className="sleep-diary-gcard__glow" />

    {/* Content wrapper sits above the glow */}
    <div className="sleep-diary-gcard__content">
      {children}
    </div>
  </div>
);

/**
 * Pill — small outlined badge used to display wake-episode time and duration.
 *
 * @param {{ children: React.ReactNode }} props
 */
const Pill = ({ children }) => (
  <span className="sleep-diary-wake-card__pill">
    {children}
  </span>
);

/**
 * BackButton — fixed "Back to Resident Details" button shown in the top-left
 * corner while navigating from a resident profile to this sleep diary page.
 *
 * Extracted to avoid copy-pasting the same JSX across the loading, error,
 * and main render states.
 *
 * @param {{ onClick: () => void, isMobile: boolean }} props
 */
const BackButton = ({ onClick, isMobile }) => (
  <button
    onClick={onClick}
    className={`sleep-diary-back-button ${isMobile ? 'mobile' : ''}`}
  >
    {/* Left-pointing arrow icon */}
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
    {isMobile ? "Back" : "Back to Resident Details"}
  </button>
);

/* ─────────────────────────────────────────────────────────────────────────
   Custom Recharts shapes
   ───────────────────────────────────────────────────────────────────────── */

/**
 * LollipopBar — custom Recharts bar shape rendered as a thin vertical line
 * with a filled white circle on top (lollipop style).
 *
 * Used in the Body Movement bar chart to reduce visual weight compared to
 * a solid bar.
 *
 * @param {{ x: number, y: number, width: number, height: number }} props
 */
const LollipopBar = ({ x, y, width, height }) => {
  const cx = x + width / 2;
  return (
    <g>
      {/* Stem */}
      <line x1={cx} y1={y + height} x2={cx} y2={y + 6}
            stroke="#ffffff" strokeWidth={2} />
      {/* Head */}
      <circle cx={cx} cy={y + 4} r={5} fill="#ffffff" />
    </g>
  );
};

/**
 * SimpleDot — plain white circle used as the data-point dot on the Sleep
 * Duration line chart. Keeps the chart clean without quality-indicator icons.
 *
 * @param {{ cx: number, cy: number }} props  (injected by Recharts)
 */
const SimpleDot = ({ cx, cy }) => (
  <circle cx={cx} cy={cy} r={5}
          fill="#ffffff" stroke="#ffffff" strokeWidth={1} />
);

/* ─────────────────────────────────────────────────────────────────────────
   Page component
   ───────────────────────────────────────────────────────────────────────── */

/**
 * SleepDiaryPage — full-page sleep dashboard for a single resident.
 *
 * @param {{
 *   residentId?:       string,   // Overrides the default demo resident
 *   showBackButton?:   boolean,  // Show the fixed back-navigation button
 *   onBackToResident?: () => void
 * }} props
 */
export default function SleepDiaryPage({ residentId, showBackButton, onBackToResident }) {
  const [sleepData, setSleepData] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  const { isMobile, isTablet, isDesktop } = useWindowSize();

    // Use the provided residentId - no default fallback
  const activeResidentId = residentId;

    /**
   * Fetch sleep data for `activeResidentId` from the API.
   * Wrapped in `useCallback` so that `useEffect` can list it as a stable
   * dependency without triggering an infinite loop.
   */
  const loadSleepData = useCallback(async () => {
    // Don't load if no resident is selected
    if (!activeResidentId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getSleepData(activeResidentId);
      setSleepData(data);
    } catch (err) {
      console.error("Failed to load sleep data for resident:", activeResidentId, err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [activeResidentId]);

  // Re-fetch whenever the target resident changes
  useEffect(() => { loadSleepData(); }, [loadSleepData]);

    /* ── Loading state ── */
    if (loading) {
      return (
        <main className="sleep-diary-center-layout">
          {showBackButton && onBackToResident && <BackButton onClick={onBackToResident} isMobile={isMobile} />}
          <LoadingSpinner message="Loading sleep data..." />
        </main>
      );
    }

                /* ── Error / no-data state ── */
  
                                // If no resident selected, show selection prompt
                if (!activeResidentId) {
                  return (
                    <main className="sleep-diary-center-layout">
                      {showBackButton && onBackToResident && <BackButton onClick={onBackToResident} isMobile={isMobile} />}
                      <div className="sleep-diary-empty-state">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                          <circle cx="12" cy="7" r="4"/>
                        </svg>
                        <p className="sleep-diary-empty-state__title">No Resident Selected</p>
                        <p className="sleep-diary-empty-state__subtitle">Please select a resident from the dropdown in the navigation bar</p>
                      </div>
                    </main>
                  );
                }
  
                // Check if there's an actual error (not just missing data)
                // If sleepData is null, it means 404 (no data), which is not an error
                if (error && sleepData !== null) {
                  // Real error (network, 500, etc.) - show error message
                    return (
                      <main className="sleep-diary-center-layout">
                        {showBackButton && onBackToResident && <BackButton onClick={onBackToResident} isMobile={isMobile} />}
                        <ErrorMessage error={error} onRetry={loadSleepData} residentId={activeResidentId} />
                      </main>
                    );
                }
  
                // If we reach here, either we have data OR sleepData is null (404 - no data available)
                // In both cases, we'll render the page with empty state if needed

    /* ─────────────────────────────────────────────────────────────────────
       Data transformations — convert API shapes into Recharts-friendly arrays
       ───────────────────────────────────────────────────────────────────── */

    // Use empty data if sleepData is null (no data available)
    const dataToUse = !sleepData ? {
    metrics: {
      totalSleepTime: { value: '--', unit: 'hrs', change: 0 },
      sleepEfficiency: { value: '--', unit: '%', change: 0 },
      wakeAfterSleepOnset: { value: '--', unit: 'min', change: 0 },
      sleepLatency: { value: '--', unit: 'min', change: 0 },
    },
    sleepStages: {
      remSleep: '--',
      lightSleep: '--',
      deepSleep: '--',
      totalMinutes: 0,
    },
    bodyMovement: [],
    sleepDurationOverTime: [],
    wakeEpisodes: [],
  } : sleepData;

    /** Sleep duration points: { day, hours, q } — one entry per tracked day. */
  const sleepDurationData = dataToUse.sleepDurationOverTime.map(d => ({
    day: d.day, hours: d.hours, q: d.quality,
  }));

  /**
   * Calculate dynamic Y-axis range for sleep duration chart
   * Ensures the chart scales appropriately for any sleep duration
   */
  const maxSleepHours = sleepDurationData.length > 0 ? Math.max(...sleepDurationData.map(d => d.hours), 8) : 8;
  const sleepYAxisMax = Math.ceil(maxSleepHours); // Round up to nearest whole hour
  const sleepYAxisTicks = Array.from({ length: sleepYAxisMax + 1 }, (_, i) => i);

  /** Body-movement samples: { t (time label), h (movement value) }. */
  const movementData = dataToUse.bodyMovement.map(d => ({
    t: d.time, h: d.value,
  }));

  /**
   * Wake-episode rows aligned to the fixed overnight timeline.
   * Slots with no recorded episode get `dur: 0` and `hasData: false`
   * so the chart still renders a faint placeholder line for that hour.
   */
  const wakeEpisodes = SLEEP_TIMELINE.map(slot => {
    const episode = dataToUse.wakeEpisodes.find(e => e.hourLabel === slot.label);
    return {
      label:    slot.label,
      dur:      episode ? episode.duration  : 0,
      wakeTime: episode ? episode.wakeTime  : "",
      wakeDur:  episode ? episode.wakeDur   : "",
      hasData:  !!episode,
    };
  });

    /** Sleep-stage segments for the donut chart. */
  const allStagesData = [
    { name: "REM Sleep",   value: dataToUse.sleepStages.remSleep,   color: "#5bb8e8" },
    { name: "Light Sleep", value: dataToUse.sleepStages.lightSleep, color: "#c5e4f3" },
    { name: "Deep Sleep",  value: dataToUse.sleepStages.deepSleep,  color: "#1a6fb5" },
  ];

  // Filter out stages with 0 or '--' values for the legend and labels
  const stagesData = allStagesData.filter(stage => 
    stage.value !== 0 && stage.value !== '--' && stage.value !== '0'
  );

  // Check if all sleep stages are zero
  const allStagesZero = stagesData.length === 0 && sleepData;

  // If all stages are zero, create a white donut
  const donutData = allStagesZero 
    ? [{ name: "No Data", value: 100, color: "#ffffff" }]
    : stagesData.length > 0 
      ? stagesData 
      : allStagesData; // Fallback to show all if no data

    // Derive human-readable totals shown below the donut
  const totalSleepHours   = Math.floor(dataToUse.sleepStages.totalMinutes / 60);
  const remainingSleepMinutes = dataToUse.sleepStages.totalMinutes % 60;
  const totalSleepMinutes = dataToUse.sleepStages.totalMinutes;

    /** Four KPI cards in the left column. */
  const metricCards = [
        { label: "Total Sleep Time",        val: String(dataToUse.metrics.totalSleepTime.value),       unit: !sleepData ? '' : dataToUse.metrics.totalSleepTime.unit,       pct: !sleepData ? '' : `${dataToUse.metrics.totalSleepTime.change}%`,       icon: totalSleepIcon },
    { label: "Sleep Efficiency",        val: String(dataToUse.metrics.sleepEfficiency.value),      unit: !sleepData ? '' : dataToUse.metrics.sleepEfficiency.unit,      pct: !sleepData ? '' : `${dataToUse.metrics.sleepEfficiency.change}%`,      icon: efficiencyIcon },
    { label: "Wake After Sleep\nOnset", val: String(dataToUse.metrics.wakeAfterSleepOnset.value),  unit: !sleepData ? '' : dataToUse.metrics.wakeAfterSleepOnset.unit,  pct: !sleepData ? '' : `${dataToUse.metrics.wakeAfterSleepOnset.change}%`,  icon: wasoIcon },
    { label: "Sleep Latency",           val: String(dataToUse.metrics.sleepLatency.value),         unit: !sleepData ? '' : dataToUse.metrics.sleepLatency.unit,         pct: !sleepData ? '' : `${dataToUse.metrics.sleepLatency.change}%`,         icon: latencyIcon },
  ];

    /* ─────────────────────────────────────────────────────────────────────
     Responsive sizing — derived from the current breakpoint
     ───────────────────────────────────────────────────────────────────── */

    const gap         = isMobile ? 12 : 16;
  const threeCol    = isDesktop;
  const donutInner  = isMobile ? 40 : isTablet ? 50 : 60;
  const donutOuter  = isMobile ? 58 : isTablet ? 68 : 82;
  const donutHeight = isMobile ? 140 : isTablet ? 170 : 200;
  const wakeRightW  = isMobile ? 60 : isTablet ? 80 : 110;

  /* ─────────────────────────────────────────────────────────────────────
     Render
     ───────────────────────────────────────────────────────────────────── */

    return (
    <main style={{
      flex: 1,
      padding: isMobile ? "12px" : "16px 40px",
      // Extra top padding keeps content below the fixed navbar (and back button)
      paddingTop: isMobile ? (showBackButton ? 130 : 75) : (showBackButton ? 140 : 82),
      paddingBottom: isMobile ? 16 : 12,
      overflowY: "auto",
      display: "flex", flexDirection: "column",
      gap, minWidth: 0,
      position: "relative", zIndex: 1,
      marginTop: 0,
    }}>

            {/* Fixed back-navigation button (only when navigating from a resident profile) */}
            {showBackButton && onBackToResident && <BackButton onClick={onBackToResident} isMobile={isMobile} />}

                        {/* ── Responsive main grid: 1 col (mobile) / 2 col (tablet) / 3 col (desktop) ── */}
      <div className={`sleep-diary-grid ${isMobile ? 'mobile' : isTablet ? 'tablet' : isDesktop ? 'desktop' : ''}`}>

                {/* ══════════════════════════════════════════════════════════════
            LEFT COLUMN — Four KPI metric cards
            On tablet the column spans the full width to display 2 × 2.
            ══════════════════════════════════════════════════════════════ */}
        <div className={`sleep-diary-metrics-column ${isMobile ? 'mobile' : isTablet && !threeCol ? 'tablet' : ''}`}>
                    {metricCards.map(({ label, val, unit, pct, icon }) => (
            <div key={label} className={`sleep-diary-metric-card ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
                            {/* Row 1: metric label (left) + percentage change badge (right) */}
              <div className={`sleep-diary-metric-card__header ${isMobile ? 'mobile' : ''}`}>
                <span className={`sleep-diary-metric-card__label ${isMobile ? 'mobile' : ''}`}>
                  {label}
                </span>

                                                                {/* Circular badge: change % + downward arrow - always render to maintain layout */}
                                <div className={`sleep-diary-metric-card__badge ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''} ${!pct ? 'sleep-diary-metric-card__badge--hidden' : ''}`}>
                                  <span className={`sleep-diary-metric-card__badge-text ${isMobile ? 'mobile' : ''}`}>
                                    {pct || '0%'}
                                  </span>
                                  {/* Down-right arrow — indicates direction of change */}
                                  <svg className="sleep-diary-metric-card__badge-arrow" viewBox="0 0 16 16" fill="none">
                                    <path d="M4 4l8 8M12 5v7H5"
                                          stroke="#ff4444" strokeWidth="2.5"
                                          strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </div>
              </div>

                            {/* Row 2: decorative icon (left) + numeric value + unit (right) */}
              <div className={`sleep-diary-metric-card__body ${isMobile ? 'mobile' : ''}`}>
                <img src={icon} alt={label} className={`sleep-diary-metric-card__icon ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`} />
                <div className="sleep-diary-metric-card__value-container">
                  <span className={`sleep-diary-metric-card__value ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
                    {val}
                  </span>
                  <span className={`sleep-diary-metric-card__unit ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
                    {unit}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

                {/* ══════════════════════════════════════════════════════════════
            MIDDLE COLUMN — Sleep Stages donut + Body Movement bar chart
            ══════════════════════════════════════════════════════════════ */}
        <div className={`sleep-diary-middle-column ${isMobile ? 'mobile' : ''}`}>

                                        {/* Sleep Stages Distribution — three-segment donut */}
                                                            <GCard className={`sleep-diary-stages-card ${isMobile ? 'mobile' : isTablet ? 'tablet' : isDesktop ? 'desktop' : ''}`} isMobile={isMobile}>
            <div className="sleep-diary-stages-card__header">
              <p className={`sleep-diary-stages-card__title ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
                Sleep Stages Distribution
              </p>
              
                            {/* Sleep Stages Legend - Top Right */}
              {sleepData && !allStagesZero && stagesData.length > 0 && (
                <div className="sleep-diary-stages-card__legend">
                  {stagesData.map((stage) => (
                    <div key={stage.name} className="sleep-diary-stages-card__legend-item">
                      <span 
                        className="sleep-diary-stages-card__legend-color" 
                        style={{ backgroundColor: stage.color }}
                      />
                      <span className="sleep-diary-stages-card__legend-label">{stage.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

                {/* Deep Sleep label — Donut — REM Sleep label (horizontal row) */}
              {!sleepData ? (
              <div className={`sleep-diary-stages-card__empty ${isMobile ? 'mobile' : ''}`}>
                No data available for Sleep Stages Distribution
              </div>
              ) : (
              <div className="sleep-diary-stages-card__chart-container">
                {/* Deep Sleep label (left) - only show if value > 0 */}
                {!allStagesZero && stagesData.some(s => s.name === "Deep Sleep") && (
                  <>
                    <div className="sleep-diary-stages-card__label-left">
                      <span className="sleep-diary-stages-card__label-name">Deep Sleep</span>
                      <span className="sleep-diary-stages-card__label-value">
                        {dataToUse.sleepStages.deepSleep}%
                      </span>
                    </div>
                    {/* Connector line (left) */}
                    <div className="sleep-diary-stages-card__connector" />
                  </>
                )}

                                                                {/* Donut chart */}
                <div className="sleep-diary-stages-card__donut-wrapper">
                  <ResponsiveContainer width="100%" height={donutHeight}>
                    <PieChart>
                      <Pie
                        data={donutData}
                        cx="50%" cy="50%"
                        innerRadius={donutInner} outerRadius={donutOuter}
                        startAngle={90} endAngle={-270}
                        dataKey="value" strokeWidth={0}
                      >
                        {donutData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>

                                    {/* Soft glow underneath the donut — decorative only */}
                  <div className="sleep-diary-stages-card__donut-glow" />
                </div>

                {/* Connector line (right) - only show if REM Sleep > 0 */}
                {!allStagesZero && stagesData.some(s => s.name === "REM Sleep") && (
                  <>
                    <div className="sleep-diary-stages-card__connector" />
                    {/* REM Sleep label (right) */}
                    <div className="sleep-diary-stages-card__label-right">
                      <span className="sleep-diary-stages-card__label-name">REM Sleep</span>
                      <span className="sleep-diary-stages-card__label-value">
                        {dataToUse.sleepStages.remSleep}%
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Light Sleep label — sits below the donut, connected by a vertical line - only show if value > 0 */}
            {sleepData && !allStagesZero && stagesData.some(s => s.name === "Light Sleep") && (
              <div className="sleep-diary-stages-card__light-sleep">
                <div className="sleep-diary-stages-card__light-sleep-connector" />
                <span className="sleep-diary-stages-card__light-sleep-value">
                  {dataToUse.sleepStages.lightSleep}%
                </span>
                <span className="sleep-diary-stages-card__light-sleep-label">
                  Light Sleep
                </span>
              </div>
            )}

                {!sleepData ? (
                <p className="sleep-diary-stages-card__total sleep-diary-stages-card__total--empty">
                  {/* Message already shown in chart area */}
                </p>
                            ) : (
                <p className="sleep-diary-stages-card__total">
                  Total sleep time of {totalSleepHours} hours {remainingSleepMinutes} minutes ({totalSleepMinutes} minutes)
                </p>
              )}
          </GCard>

          {/* Body Movement Analysis — lollipop bar chart */}
            <GCard className={`sleep-diary-movement-card ${isMobile ? 'mobile' : isTablet ? 'tablet' : isDesktop ? 'desktop' : ''}`} isMobile={isMobile}>
            <div className="sleep-diary-movement-card__header">
              <p className={`sleep-diary-movement-card__title ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
                Body Movement Analysis
              </p>

              {/* Movement intensity legend */}
              <div className="sleep-diary-movement-card__legend">
                {MOVEMENT_LEGEND.map(([color, label]) => (
                  <span key={label} className="sleep-diary-movement-card__legend-item">
                    <span className="sleep-diary-movement-card__legend-color" style={{ background: color }} />
                    {label}
                  </span>
                ))}
              </div>
            </div>

            <div className="sleep-diary-movement-card__chart-wrapper">
              {/* Vertical colour gradient bar — mirrors the legend visually */}
              <div className="sleep-diary-movement-card__gradient-bar" />

              {!sleepData || movementData.length === 0 ? (
                <div className={`sleep-diary-movement-card__empty ${isMobile ? 'mobile' : ''}`}>
                  No data available for Body Movement Analysis
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={movementData}
                    barCategoryGap="30%"
                    margin={{ bottom: 0, top: 12, left: 0, right: 4 }}
                  >
                                        <XAxis
                      dataKey="t"
                      tick={{ fill: "#ffffff", fontSize: isMobile ? 9 : 11, fontStyle: "italic" }}
                      axisLine={{ stroke: "rgba(255,255,255,0.15)" }}
                      tickLine={false}
                    />
                    <YAxis hide />
                    <Bar dataKey="h" shape={<LollipopBar />} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </GCard>
        </div>

                {/* ══════════════════════════════════════════════════════════════
            RIGHT COLUMN — Sleep Duration line chart + Wake Episodes timeline
            ══════════════════════════════════════════════════════════════ */}
        <div className={`sleep-diary-right-column ${isMobile ? 'mobile' : ''}`}>

                                                                                {/* Sleep Duration Over Time — line chart */}
                                        <GCard className={`sleep-diary-duration-card ${isMobile ? 'mobile' : isTablet ? 'tablet' : isDesktop ? 'desktop' : ''}`} isMobile={isMobile}>
                        <div className="sleep-diary-duration-card__header">
              <p className={`sleep-diary-duration-card__title ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
                Sleep Duration Over Time
              </p>
              <p className="sleep-diary-duration-card__subtitle">
                Hrs
              </p>
            </div>

                                                <div className={`sleep-diary-duration-card__chart-wrapper ${!threeCol ? 'fixed-height' : ''}`}>
              {!sleepData || sleepDurationData.length === 0 ? (
                <div className={`sleep-diary-duration-card__empty ${isMobile ? 'mobile' : ''}`}>
                  No data available for Sleep Duration Over Time
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={sleepDurationData}
                    margin={{ top: 8, right: 12, bottom: 18, left: 0 }}
                  >
                    <CartesianGrid
                      stroke="rgba(255,255,255,0.15)"
                      strokeDasharray="4 4"
                      vertical={false}
                    />
                                        <XAxis
                      dataKey="day"
                      tick={{ fill: "#ffffff", fontSize: 11, fontWeight: 600 }}
                      axisLine={{ stroke: "#ffffff", strokeWidth: 1.5 }}
                      tickLine={false}
                      label={{
                        value: "Days", position: "insideBottomLeft",
                        dx: -4, dy: 14,
                        fill: "#ffffff", fontSize: 11, fontStyle: "italic",
                      }}
                    />
                                        <YAxis
                      domain={[0, sleepYAxisMax]} ticks={sleepYAxisTicks}
                      tick={{ fill: "#ffffff", fontSize: 11 }}
                      axisLine={{ stroke: "#ffffff", strokeWidth: 1.5 }}
                      tickLine={false} width={22}
                    />
                    <Line
                      type="linear" dataKey="hours"
                      stroke="#ffffff" strokeWidth={2}
                      dot={<SimpleDot />}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </GCard>

                                                                                {/* Wake Episodes — horizontal lollipop timeline (10 PM – 6 AM) */}
                                        <GCard className={`sleep-diary-wake-card ${isMobile ? 'mobile' : isTablet ? 'tablet' : isDesktop ? 'desktop' : ''}`} isMobile={isMobile}>
            <div className="sleep-diary-wake-card__header">
              <p className={`sleep-diary-wake-card__title ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
                Wake Episodes
              </p>

              {/* Time / Duration table header + data rows */}
              <div className="sleep-diary-wake-card__data-table">
                <div className="sleep-diary-wake-card__data-header">
                  <span className="sleep-diary-wake-card__data-header-label">
                    Time
                  </span>
                  <span className="sleep-diary-wake-card__data-header-label sleep-diary-wake-card__data-header-label--duration">
                    Duration
                  </span>
                </div>

                                {/* Only show rows for hours that actually had a recorded wake event */}
                {wakeEpisodes
                  .filter(e => e.hasData && e.wakeTime)
                  .map(({ wakeTime, wakeDur }, i) => (
                    <div key={i} className="sleep-diary-wake-card__data-row">
                      <Pill>{wakeTime}</Pill>
                      <Pill>{wakeDur}</Pill>
                    </div>
                  ))
                }
              </div>
            </div>

                                                <div className="sleep-diary-wake-card__timeline">
              {!sleepData || wakeEpisodes.every(e => !e.hasData) ? (
                <div className={`sleep-diary-wake-card__empty ${isMobile ? 'mobile' : ''}`}>
                  No data available for Wake Episodes
                </div>
              ) : (
                <>
                  {/* One row per hour slot */}
                  <div className="sleep-diary-wake-card__timeline-rows">
                                        {wakeEpisodes.map(({ label, dur, hasData }, idx) => (
                      <div key={idx} className="sleep-diary-wake-card__timeline-row">
                        {/* Hour label */}
                        <span className="sleep-diary-wake-card__timeline-label">
                          {label}
                        </span>

                        <div className="sleep-diary-wake-card__timeline-bar-container">
                                                    {hasData && dur > 0 ? (
                            <>
                              {/* Line width is proportional to duration (max 60 min = 100%) */}
                              <div className="sleep-diary-wake-card__timeline-bar" style={{ width: `${(dur / 60) * 100}%` }} />
                              {/* Terminal circle at the end of the line */}
                              <div className="sleep-diary-wake-card__timeline-circle" />
                              {/* Wake icon after the circle */}
                              <img src={wakeIcon} alt="Wake" className={`sleep-diary-wake-card__timeline-icon ${isMobile ? 'mobile' : ''}`} />
                            </>
                          ) : (
                            /* Faint placeholder line for hours with no wake event */
                            <div className="sleep-diary-wake-card__timeline-placeholder" />
                          )}
                        </div>

                                                {/* Right spacer keeps the chart area away from the pill table */}
                        <div className={`sleep-diary-wake-card__timeline-spacer ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`} />
                      </div>
                    ))}
                  </div>

                                    {/* Duration ruler: 0 – 60 min tick labels */}
                  <div className="sleep-diary-wake-card__ruler" style={{ paddingRight: wakeRightW }}>
                    {WAKE_DURATION_TICKS.map(tick => (
                      <span key={tick} className={`sleep-diary-wake-card__ruler-tick ${isMobile ? 'mobile' : ''}`}>
                        {tick}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </GCard>
        </div>

      </div>
    </main>
  );
}

